import { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/pages/admin/AdminLayout";
import ImageUploadField from "@/components/ImageUploadField";
import { AccessControlCard } from "@/components/admin/AccessControlCard";
import { TwoFactorCard } from "@/components/admin/TwoFactorCard";
import { LoginAuditCard } from "@/components/admin/LoginAuditCard";

const SECTIONS = [
  {
    key: "home_hero",
    title: "Home — Hero",
    help: "The first thing visitors see, over the ocean photo.",
    fields: [
      { name: "home_hero_tagline", label: "Small tagline", type: "input" },
      { name: "home_hero_heading", label: "Headline (wrap a word in *asterisks* for gold emphasis)", type: "input" },
      { name: "home_hero_subtext", label: "Subtext", type: "textarea" },
    ],
  },
  {
    key: "home_about",
    title: "Home — About the Author",
    help: "Sits beside your portrait on the home page.",
    fields: [
      { name: "home_about_heading", label: "Heading", type: "input" },
      { name: "home_about_paragraph1", label: "Paragraph 1", type: "textarea" },
      { name: "home_about_paragraph2", label: "Paragraph 2", type: "textarea" },
    ],
  },
  {
    key: "books",
    title: "Books page",
    help: "Intro copy above your library grid.",
    fields: [
      { name: "books_heading", label: "Heading (wrap a word in *asterisks* for gold emphasis)", type: "input" },
      { name: "books_subtext", label: "Subtext", type: "textarea" },
    ],
  },
  {
    key: "journal",
    title: "Journal page",
    help: "Intro copy above your list of entries.",
    fields: [
      { name: "journal_heading", label: "Heading (wrap a word in *asterisks* for gold emphasis)", type: "input" },
      { name: "journal_subtext", label: "Subtext", type: "textarea" },
    ],
  },
  {
    key: "contact",
    title: "Contact page",
    help: "The letter-writing invitation readers see.",
    fields: [
      { name: "contact_heading", label: "Heading (wrap a word in *asterisks* for gold emphasis)", type: "input" },
      { name: "contact_subtext", label: "Subtext", type: "textarea" },
      { name: "contact_tagline", label: "Small tagline", type: "input" },
    ],
  },
  {
    key: "healing",
    title: "Space for Healing Together",
    help: "The members-only healing section — its hero wording over the ocean image.",
    fields: [
      { name: "healing_eyebrow", label: "Small eyebrow label", type: "input" },
      { name: "healing_heading", label: "Headline (wrap a word in *asterisks* for gold emphasis)", type: "input" },
      { name: "healing_subtext", label: "Subtext", type: "textarea" },
    ],
  },
];

export default function Settings() {
  const [portraitUrl, setPortraitUrl] = useState("");
  const [heroImageUrl, setHeroImageUrl] = useState("");
  const [healingImageUrl, setHealingImageUrl] = useState("");
  const [content, setContent] = useState({});
  const [saving, setSaving] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/settings")
      .then(({ data }) => {
        setPortraitUrl(data.portrait_url || "");
        setHeroImageUrl(data.hero_image_url || "");
        setHealingImageUrl(data.healing_image_url || "");
        setContent(data);
      })
      .finally(() => setLoading(false));
  }, []);

  const setPortrait = async (newUrl) => {
    const prev = portraitUrl;
    setPortraitUrl(newUrl);
    try {
      await api.put("/settings", { portrait_url: newUrl });
      if (newUrl !== prev) toast.success("Portrait updated.");
    } catch (err) {
      setPortraitUrl(prev);
      toast.error(formatApiError(err.response?.data?.detail, "Could not save."));
    }
  };

  const setHeroImage = async (newUrl) => {
    const prev = heroImageUrl;
    setHeroImageUrl(newUrl);
    try {
      await api.put("/settings", { hero_image_url: newUrl });
      if (newUrl !== prev) toast.success("Hero image updated.");
    } catch (err) {
      setHeroImageUrl(prev);
      toast.error(formatApiError(err.response?.data?.detail, "Could not save."));
    }
  };

  const setHealingImage = async (newUrl) => {
    const prev = healingImageUrl;
    setHealingImageUrl(newUrl);
    try {
      await api.put("/settings", { healing_image_url: newUrl });
      if (newUrl !== prev) toast.success("Healing hero image updated.");
    } catch (err) {
      setHealingImageUrl(prev);
      toast.error(formatApiError(err.response?.data?.detail, "Could not save."));
    }
  };

  const saveSection = async (section) => {
    setSaving((s) => ({ ...s, [section.key]: true }));
    try {
      const payload = {};
      section.fields.forEach((f) => { payload[f.name] = content[f.name] || ""; });
      await api.put("/settings", payload);
      toast.success("Saved.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not save."));
    } finally {
      setSaving((s) => ({ ...s, [section.key]: false }));
    }
  };

  return (
    <AdminLayout title="Settings">
      <Toaster position="top-center" />
      <p className="text-[#5C6670] font-light max-w-2xl">
        Manage every piece of public-facing text and imagery yourself — no code changes needed. Leave a field blank and it falls back to the original wording.
      </p>

      {loading ? (
        <div className="text-sm text-[#5C6670] mt-10">Loading…</div>
      ) : (
        <div className="mt-10 space-y-10 max-w-3xl">
          <div className="bg-white border border-[#E5E5E5] p-8" data-testid="settings-portrait-card">
            <div className="gv-label">Home — About the Author</div>
            <h3 className="font-serif text-2xl mt-2 text-[#1A1F24] mb-6">Your portrait</h3>
            <ImageUploadField
              value={portraitUrl}
              onChange={setPortrait}
              label="Author portrait"
              testIdPrefix="settings-portrait"
              aspect="aspect-[3/4]"
              helpText="JPG / PNG / WEBP up to 5 MB. Appears on the home page in the About section."
            />
          </div>

          <div className="bg-white border border-[#E5E5E5] p-8" data-testid="settings-hero-image-card">
            <div className="gv-label">Home — Hero background</div>
            <h3 className="font-serif text-2xl mt-2 text-[#1A1F24] mb-6">Ocean hero image</h3>
            <ImageUploadField
              value={heroImageUrl}
              onChange={setHeroImage}
              label="Hero background image"
              testIdPrefix="settings-hero-image"
              aspect="aspect-video"
              helpText="JPG / PNG / WEBP up to 5 MB. The full-bleed photo behind your home page headline. Leave empty to keep the default calming ocean image."
            />
          </div>

          <div className="bg-white border border-[#E5E5E5] p-8" data-testid="settings-healing-image-card">
            <div className="gv-label">Space for Healing Together — Hero background</div>
            <h3 className="font-serif text-2xl mt-2 text-[#1A1F24] mb-6">Healing hero image</h3>
            <ImageUploadField
              value={healingImageUrl}
              onChange={setHealingImage}
              label="Healing hero background image"
              testIdPrefix="settings-healing-image"
              aspect="aspect-video"
              helpText="JPG / PNG / WEBP up to 5 MB. The calming photo behind the Healing Space headline. Leave empty for the default ocean image."
            />
          </div>

          {SECTIONS.map((section) => (
            <div key={section.key} className="bg-white border border-[#E5E5E5] p-8" data-testid={`settings-section-${section.key}`}>
              <div className="gv-label">{section.title}</div>
              <p className="text-sm text-[#5C6670] mt-2 mb-6">{section.help}</p>
              <div className="space-y-5">
                {section.fields.map((f) => (
                  <div key={f.name}>
                    <label className="gv-label">{f.label}</label>
                    {f.type === "textarea" ? (
                      <textarea
                        rows={3}
                        className="gv-input mt-2 resize-none"
                        value={content[f.name] || ""}
                        onChange={(e) => setContent((c) => ({ ...c, [f.name]: e.target.value }))}
                        data-testid={`settings-field-${f.name}`}
                      />
                    ) : (
                      <input
                        type="text"
                        className="gv-input mt-2"
                        value={content[f.name] || ""}
                        onChange={(e) => setContent((c) => ({ ...c, [f.name]: e.target.value }))}
                        data-testid={`settings-field-${f.name}`}
                      />
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => saveSection(section)}
                  disabled={saving[section.key]}
                  className="gv-btn-gold"
                  data-testid={`settings-save-${section.key}`}
                >
                  {saving[section.key] ? "Saving…" : "Save changes"}
                </button>
              </div>
            </div>
          ))}
          <AccessControlCard />
          <TwoFactorCard />
          <LoginAuditCard />
        </div>
      )}
    </AdminLayout>
  );
}
